/* Manufacturing Insights — plain-English glossary for the info bubbles (InsInfoTip).
 * Flat { termKey: helpText } map spread over the cross-report COMMON_TIPS, matching the design-system
 * convention (see Insights/insightsGlossary.js + README). MI uses InsInfoTip mode="click" for its ⓘ
 * popovers. Metric-level entries mirror the backend MiFreshness.ProvenanceMetrics() `basis` text, condensed.
 * Consumed as:  <InsInfoTip term-key="late_promise" :glossary="fieldTips" mode="click" />
 */
import { COMMON_TIPS } from "../Insights/insightsGlossary";

const fieldTips = {
  ...COMMON_TIPS,

  // ---- KPI cards / chart titles ----
  parts_sales: "Company-wide invoiced parts revenue by month — receivable lines with a part id, net of credit memos (VMFG).",
  machine_shop: "Machine-shop receipts $ per machinist hour vs goal (higher is better), from the monthly Shop 'YYYY Monthly Summary' workbook.",
  shipping_otd: "Ship-on-time %. SYSTEM = shipped on/before the current (revised) desired date; REAL = shipped on/before the original promise recovered from the ship-date audit.",
  rma: "Return Material Authorizations created per month (trailing 13 months), from RMA-type customer orders in VMFG. Click a month for its returned parts.",
  rma_reasons: "Why returns were authorized. Coded reason & department are historical (≤2024, VISUAL reason entry lapsed); current returns carry a free-text reason from the CS RMA LOG.",
  hidden_lateness: "Orders on-time against the revised date but late vs the original promise — the date was pushed out to match the ship. The gap between SYSTEM and REAL on-time.",
  speed_mix: "Each shipment bucketed by order → ship elapsed days (Next day / 2–3 d / 4–14 d / 14+ d), as a share of the month's shipments.",
  late_by_dept: "Trailing-13-month count of orders shipped late (vs original promise) attributed to the responsible area (by routing, not a formal blame code).",

  // ---- Shipping drill: order-level detail ----
  order: "Customer order / shipment id.",
  customer: "Customer the order shipped to.",
  rep: "Sales rep on the order.",
  ship_via: "Carrier / ship method.",
  orig_promise: "The date first promised to the customer, recovered from the desired-ship-date audit. The honest promise.",
  revised: "The current desired ship date — possibly changed after the original promise.",
  actual_ship: "Date the order actually shipped.",
  late_promise: "Days shipped after the ORIGINAL promise (the real lateness). ≤0 = on time to promise.",
  days_pushed: "How far the desired ship date was moved out from the original promise.",
  changes: "Number of times the desired ship date was changed.",
  changed_by: "Who last changed the desired ship date (from the audit).",
  status: "On-time classification vs the original promise / revised date.",

  // ---- Late Shipments by Department ----
  department: "Responsible area the late order is attributed to (by routing, not a formal blame code).",
  late_orders: "Count of orders shipped late (vs original promise) attributed to this department over the trailing 13 months.",

  // ---- Parts drill: invoice & line detail ----
  rank: "Row number within the month's rows.",
  invoice: "VMFG invoice number.",
  date: "Row date — invoice date on parts rows, RMA created date on RMA rows.",
  type: "Invoice type — I = invoice, M = credit memo (netted out of totals).",
  rma_no: "Return Material Authorization number.",
  rma_type: "RMA type / disposition.",
  amount: "Invoiced amount. Line amounts roll up exactly to the month's invoiced-parts total.",
  part: "Part id / description on the line.",
  family: "Part family from the part master (Wear / Breakdown / Spare; “(unclassified)” for parts with no type — the majority of parts $).",
  qty: "Quantity invoiced / returned on the line.",

  // ---- RMA drill: returned-parts detail ----
  reason: "Why the part came back. Coded reason is historical (≤2024); current returns carry a free-text reason from the CS RMA LOG.",
  dept: "Responsible department (historical coding, ≤2024).",

  // ---- Working Capital: excluded-parts detail ----
  wc_exclusion_reason:
    "Which of Accounting's four inclusion rules removed this part. NOT_STOCKED = not marked Stocked in Part " +
    "Maintenance (foundry raw material, tooling, and a long tail of low-value non-wear parts). PUR_PREFIX = " +
    "part id begins \"PUR\". CR_SUFFIX = part id ends \"CR\" (mostly foundry sand cores). FINISH_CODE = the " +
    "5th character of the part id is outside Accounting's list. Rules apply in that order, so a part failing " +
    "several shows only the first.",
  wc_idle_years:
    "Years since this part's last inventory transaction, from the VMFG ledger (which reaches back to 1999). " +
    "\"never moved\" means no transaction has ever been recorded against it.",

  // ---- Part Cost Variance (per-part cost per piece, month over month) ----
  pcv_rule:
    "±10% is J.C. Steele's own allowance — how far a part's cost per piece may move from the last month it had " +
    "activity before someone should look at it. It's a rule agreed with the plant, not a VISUAL setting; VISUAL " +
    "doesn't flag cost movement on its own.",
  pcv_basis:
    "Which cost we're tracking. \"Sold\" is what the parts we shipped actually cost us. \"Made or bought\" is what " +
    "it cost to put the part into stock, from work-order and purchase receipts. They're kept separate on purpose — " +
    "blending them would make a change in how much we make vs buy look like a price change. Some parts exist on " +
    "only one basis: a part we've never shipped to a customer appears only under \"made or bought\".",
  pcv_breach:
    "Parts whose cost per piece moved more than 10% up or down from the last month that part had activity. Fewer is " +
    "better. A big jump usually means a price change, a different vendor or lot size, or a costing mistake worth checking.",
  pcv_impact:
    "What this month's cost moves add up to in dollars, at this month's quantities. Positive means we spent more than " +
    "the prior month's cost would have predicted; negative means less. Closer to zero is better.",
  pcv_comparable:
    "How many parts had cost activity in at least two different months, so a comparison is possible at all. Only " +
    "these parts can fall outside the allowance.",
  pcv_median:
    "The middle month-over-month change across every comparable part. Close to zero is healthy — costs drifting " +
    "rather than lurching. A median well outside ±10% points at something shop-wide rather than one part.",
  pcv_top:
    "The part with the largest cost increase this month in DOLLARS, not percent — a 500% jump on a 50-cent bolt isn't " +
    "worth anyone's morning. Deliberately ignores three kinds of row that would otherwise win: parts with under three " +
    "months of history, moves that came with a lot-size change, and comparisons reaching back more than three months " +
    "(those are slow drift, often just inflation, rather than a sudden jump).",
  pcv_nobasis:
    "Parts we can't compare yet — this is the first month they were ever costed on this basis. These are NOT outside " +
    "the allowance; there's simply nothing to measure against. They're deliberately left out of every count and chart " +
    "on this tab.",
  pcv_period: "The calendar month the cost was posted in. Everything on this tab is grouped by month.",
  pcv_priorperiod:
    "The month this one is measured against — the most recent EARLIER month that had activity for this part. It may " +
    "not be last month, so a comparison can span a long gap. The report always tells you which month it used.",
  pcv_gap:
    "How many months back the comparison reaches. 1 means a true month-over-month move. A large number means the part " +
    "hadn't been costed for a long time, so the change is slow drift — often just inflation — rather than a sudden " +
    "jump. Most flagged parts sit above 3 months, so filter on this column when you want only recent movement.",
  pcv_unitcost:
    "Cost per piece for the month: the month's total posted cost divided by the month's quantity. Lower is better. " +
    "It's an average, not a price — it can move because of the vendor, the lot size, rework, or the mix of postings.",
  pcv_priorcost: "Cost per piece in the prior month with activity — the baseline this month is measured against.",
  pcv_changepct:
    "How far cost per piece moved from the prior month with activity. Positive means it got more expensive, so lower " +
    "is better. Anything past ±10% is outside the allowance and gets flagged.",
  pcv_change3mo:
    "How far cost per piece has moved across the last three months WITH ACTIVITY, not the last three " +
    "calendar months. Reading it beside the month-to-month Change is the point: a part can look calm every " +
    "single month and still be well up over three. Blank means the part hasn't got three months of history yet.",
  pcv_change6mo:
    "The same idea over six months of activity. This is where slow cost creep shows up — 3% a month never " +
    "trips the ±10% monthly rule but compounds to about 19% by the sixth month. Positive means more expensive, " +
    "so lower is better.",
  pcv_trendrun:
    "How many months in a row the cost has moved the same way, counting up to and including this month. " +
    "Positive is consecutive rises, negative is consecutive falls, 0 means the latest month broke the run " +
    "(or barely moved). A long run matters more than one big month: three straight rises is a trend, one " +
    "spike is usually a lot size or a one-off posting.",
  pcv_drifting:
    "Parts whose cost has moved past the ±10% allowance over six months WITHOUT any single month ever " +
    "breaking the rule. These are invisible to the month-to-month test by definition — that test only ever " +
    "sees one step at a time — so they are counted separately here rather than added to the breach total. " +
    "Lower is better. Treat a part appearing here as worth a look even though nothing has been flagged.",
  pcv_trendchart:
    "Each part's cost per piece, rebased so every line starts at 100 in its first month on the chart. A line at 130 " +
    "costs 30% more per piece than when the window opened. Rebasing is what lets a $0.42 washer and a $25,000 auger " +
    "share one axis — the lines compare how far each has moved, never how expensive they are relative to each other. " +
    "Careful with the band: here it measures the move since the START of the window, while the ±10% in the table " +
    "below is month-to-month. A part that creeps 3% a month sits well outside this band while never once tripping the " +
    "monthly rule, and that steady climb is exactly what the chart is for. Only a dozen lines are drawn, picked as " +
    "the parts that broke the allowance for the most dollars; the table below has every part.",
  pcv_changeusd:
    "How many dollars per piece the cost moved. Worth reading next to the percent — a huge percent on a cheap part is " +
    "usually not worth chasing.",
  pcv_impactcol:
    "Dollars per piece moved × this month's quantity — roughly what the move cost us, or saved us, this month. Sort " +
    "by this to put real money at the top.",
  pcv_qty:
    "Quantity with cost posted in the month. Small quantities make cost per piece jumpy, so a big percent on a tiny " +
    "quantity is weak evidence.",
  pcv_months:
    "How many separate months this part has cost activity on this basis. Two is the minimum for any comparison; under " +
    "three we flag it as thin.",
  pcv_txncount:
    "How many individual cost postings make up the month. One posting is normal for low-volume parts — it means the " +
    "month's cost per piece IS that transaction.",
  pcv_extcost: "Total cost posted for the part in the month, before dividing by quantity.",
  pcv_lot:
    "Average pieces per posting in the month. This matters: cost per piece normally falls as lot size rises, because " +
    "setup is spread over more pieces and bigger purchases get price breaks. When lot size shifts a lot between the " +
    "two months compared, some of the cost move is lot size rather than price — those rows are flagged.",
  pcv_band:
    "Where the part landed against the ±10% allowance: Over +10%, Under −10%, Within 10%, or No basis (nothing to " +
    "compare to).",
  pcv_stale:
    "How many months behind the newest cost activity this part's latest month is. A part that last had cost activity " +
    "years ago still appears here, with this column saying so.",
  pcv_reviewflag:
    "Whether anything about this row is worth a person's time — a different question from Status, which only says " +
    "whether the month broke the allowance. The first three are reasons to LOOK: Quietly drifting (crept past the " +
    "allowance over six months without any single month tripping it), Stale cost (the part's newest cost activity is " +
    "a year or more behind everyone else's), Trend run (three or more months moving the same way). The last three are " +
    "reasons the percentage may be MISLEADING rather than wrong: Old baseline, Lot size shift, Thin history. Filter " +
    "this column to work one kind at a time — \"Quietly drifting\" is the list that no other column on this tab can " +
    "produce, because a drifting part never breaks the monthly rule by definition.",
  pcv_reviewrank:
    "Queue position, 1 first. Signals rank above a clean unexplained move, and a clean unexplained move ranks above " +
    "a row carrying only caveats — because a caveat is an explanation. Within one rank the order is still biggest " +
    "dollars first. Sort by any other column to leave the queue order behind; nothing is hidden at any rank.",
  pcv_commodity: "The part's commodity code from the VISUAL part master. Blank where none is set.",
  pcv_txndate: "The date the cost was posted in VISUAL.",
  pcv_txntype: "What kind of posting it was — a work-order receipt, a purchase receipt, or a customer shipment.",
  pcv_txnsource:
    "The work order, purchase order or customer order the posting came from. There's no link out of here — look the " +
    "number up in VISUAL if you need the paperwork.",
  pcv_txnunit:
    "Cost per piece on this one posting. Flagged when it sits more than 25% away from the month's average, because a " +
    "single odd posting can pull a whole month.",
  pcv_txnvendor:
    "The vendor, on purchase receipts. Blank for work-order receipts and customer shipments, where no vendor is involved.",
  pcv_watchlist:
    "The parts the cost team asked to keep an eye on. Turning this on narrows the charts and tables to just those " +
    "parts — it does not change any number in the cards above.",
};

export default fieldTips;
